import imageCompression from "browser-image-compression";
import Loading from "components/Loading/Loading/Loading";
import { addDoc, collection, doc, updateDoc } from "firebase/firestore";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { useEffect, useState } from "react";
import { db, storage } from "../../firebase";

const WEIGHT_FORMAT_OPTIONS = [
  "싱글 스테이션",
  "이중 스테이션",
  "멀티 스테이션",
  "프리 웨이트",
  "플레이트 장착",
  "랙, 리그 및 플랫폼",
  "기능성 트레이너",
];
const CARDIO_FORMAT_OPTIONS = [
  "트레드밀",
  "어센트트레이너",
  "Elliptical",
  "ClimbMill",
  "스테퍼",
  "사이클",
  "스테어클라이머",
];

const AddModal = ({ isOpen, onClose, onSuccess, item }) => {
  const isEditMode = !!item;

  const [formData, setFormData] = useState({
    itemType: "웨이트 기구",
    name: "",
    model: "",
    series: "",
    description: "",
    format: "",
    price: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [keepImage, setKeepImage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError("");
  };

  const setItemType = (type) => {
    setFormData((prev) => ({
      ...prev,
      itemType: type,
      ...(type === "기타 기자재" ? { series: "", format: "" } : {}),
    }));
    setError("");
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setKeepImage(false);
      setError("");
    }
  };

  const handleSubmit = async () => {
    // 유효성 검사
    if (!formData.name || !formData.model) {
      setError("필수 항목을 모두 입력해주세요.");
      return;
    }

    try {
      setIsLoading(true);
      let imageUrl = item?.image || "";

      // 이미지가 있으면 압축 후 업로드
      if (!keepImage && imageFile && imageFile instanceof File) {
        // 수정 모드면 기존 이미지 삭제
        if (isEditMode && item.image) {
          try {
            const oldImageRef = ref(storage, item.image);
            await deleteObject(oldImageRef);
          } catch (err) {
            console.log("이미지 삭제 실패:", err);
          }
        }
        const options = {
          maxSizeMB: 0.1,
          maxWidthOrHeight: 900,
          useWebWorker: true,
          fileType: "image/webp",
          initialQuality: 0.8,
        };
        const compressedFile = await imageCompression(imageFile, options);

        const timestamp = Date.now();
        const imageRef = ref(storage, `estimate/items/${timestamp}.webp`);
        const metadata = {
          contentType: "image/webp",
          cacheControl: "public, max-age=31536000",
        };
        await uploadBytes(imageRef, compressedFile, metadata);
        imageUrl = await getDownloadURL(imageRef);
      }

      // Firestore에 데이터 추가

      const itemData = {
        itemType: formData.itemType,
        name: formData.name,
        model: formData.model,
        series: formData.series || "-",
        description: formData.description || "-",
        format: formData.format || "-",
        price: Number(formData.price) || 0,
        image: imageUrl,
      };

      const itemsRef = collection(db, "deploy", "estimate", "items");

      if (isEditMode) {
        // 수정
        const itemRef = doc(db, "deploy", "estimate", "items", item.id);
        await updateDoc(itemRef, {
          ...itemData,
          updatedAt: new Date().toISOString(),
        });
      } else {
        // 추가
        await addDoc(itemsRef, {
          ...itemData,
          createdAt: new Date().toISOString(),
        });
      }

      // 성공
      setFormData({
        itemType: "웨이트 기구",
        name: "",
        model: "",
        series: "",
        description: "",
        format: "",
        price: "",
      });
      setImageFile(null);
      onSuccess();
      onClose();
    } catch (err) {
      console.error("기자재 추가 실패:", err);
      setError(isEditMode ? "기자재 수정에 실패했습니다." : "기자재 추가에 실패했습니다.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // 수정모드 여부 판단 후 초기값 세팅
    if (item) {
      setFormData({
        itemType: item.itemType ?? "웨이트 기구",
        name: item.name || "",
        model: item.model || "",
        series: item.series || "",
        description: item.description || "",
        format: item.format || "",
        price: item.price?.toString() || "",
      });
      setKeepImage(!!item.image);
      setImageFile(null);
    } else {
      setFormData({
        itemType: "웨이트 기구",
        name: "",
        model: "",
        series: "",
        description: "",
        format: "",
        price: "",
      });
      setKeepImage(false);
      setImageFile(null);
    }
  }, [item, isOpen]);

  if (!isOpen) return null;

  return (
    <>
      {isLoading && <Loading />}
      <div style={styles.overlay}>
        <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
          <h2 style={styles.title}>{isEditMode ? "기자재 수정" : "기자재 추가"}</h2>
          <div style={styles.field}>
            <label style={styles.label}>구분</label>
            <div style={{ display: "flex", gap: "8px" }}>
              {["웨이트 기구", "유산소 기구", "기타 기자재"].map((type) => {
                const selected = formData.itemType === type;
                return (
                  <button
                    key={type}
                    type="button"
                    onClick={() => setItemType(type)}
                    style={{
                      flex: 1,
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: selected ? "1px solid #10134F" : "1px solid #ddd",
                      background: selected ? "#eef0ff" : "#fff",
                      fontWeight: selected ? 600 : 400,
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    {type}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={styles.form}>
            <div style={styles.field}>
              <label style={styles.label}>제품명 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                style={styles.input}
                placeholder="예: Aura Chest Press"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>
                {formData.itemType === "기타 기자재" ? "분류" : "모델"} *
              </label>
              <input
                type="text"
                value={formData.model}
                onChange={(e) => handleChange("model", e.target.value)}
                style={styles.input}
                placeholder={`예: ${formData.itemType === "기타 기자재" ? "수건" : "G3-S10"}`}
              />
            </div>
            {formData.itemType !== "기타 기자재" && (
              <>
                <div style={styles.field}>
                  <label style={styles.label}>시리즈</label>
                  <input
                    type="text"
                    value={formData.series}
                    onChange={(e) => handleChange("series", e.target.value)}
                    style={styles.input}
                    placeholder="예: Aura"
                  />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>양식</label>
                  <select
                    value={formData.format}
                    onChange={(e) => handleChange("format", e.target.value)}
                    style={styles.input}
                  >
                    <option value="">선택하세요</option>
                    {(formData.itemType === "웨이트 기구"
                      ? WEIGHT_FORMAT_OPTIONS
                      : CARDIO_FORMAT_OPTIONS
                    ).map((format) => (
                      <option key={format} value={format}>
                        {format}
                      </option>
                    ))}
                  </select>
                </div>{" "}
              </>
            )}
            <div style={styles.field}>
              <label style={styles.label}>가격 (원)</label>
              <input
                type="text"
                value={formData.price ? Number(formData.price).toLocaleString() : ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/,/g, "");
                  if (/^\d*$/.test(value)) {
                    handleChange("price", value);
                  }
                }}
                style={styles.input}
                placeholder="예: 1,000,000"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>설명</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                style={{ ...styles.input, minHeight: "40px", resize: "vertical" }}
                placeholder="제품 설명을 입력하세요"
              />
            </div>

            <div style={styles.field}>
              <label style={styles.label}>이미지</label>
              {isEditMode && keepImage && item.image ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  <img
                    src={item.image}
                    alt="현재"
                    style={{
                      width: "100%",
                      maxHeight: "100px",
                      objectFit: "contain",
                      borderRadius: "8px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setKeepImage(false)}
                    style={{ padding: "6px", fontSize: "12px", cursor: "pointer" }}
                  >
                    이미지 변경
                  </button>
                </div>
              ) : (
                <>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    style={styles.fileInput}
                  />
                  {imageFile && <p style={styles.fileName}>{imageFile.name}</p>}
                  {isEditMode && item.image && (
                    <button
                      type="button"
                      onClick={() => {
                        setKeepImage(true);
                        setImageFile(null);
                      }}
                      style={{ padding: "6px", fontSize: "12px", marginTop: "4px" }}
                    >
                      기존 이미지 유지
                    </button>
                  )}
                </>
              )}
            </div>

            {error && <p style={styles.error}>{error}</p>}

            <div style={styles.buttons}>
              <button onClick={onClose} style={styles.cancelButton} disabled={isLoading}>
                취소
              </button>
              <button onClick={handleSubmit} style={styles.submitButton} disabled={isLoading}>
                {isEditMode ? "수정" : "추가"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "24px",
    width: "500px",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  title: {
    margin: "0 0 20px 0",
    fontSize: "20px",
    fontWeight: "600",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    marginTop: "8px",
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#444",
    alignSelf: "flex-start",
    marginLeft: "0.5rem",
  },
  input: {
    padding: "10px 12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    fontSize: "14px",
    outline: "none",
  },
  fileInput: {
    fontSize: "14px",
  },
  fileName: {
    fontSize: "13px",
    color: "#666",
    margin: 0,
  },
  error: {
    color: "#e53935",
    fontSize: "13px",
    margin: 0,
  },
  buttons: {
    display: "flex",
    gap: "10px",
    marginTop: "8px",
  },
  cancelButton: {
    flex: 1,
    padding: "12px",
    borderRadius: "8px",
    border: "1px solid #ddd",
    backgroundColor: "#fff",
    cursor: "pointer",
    fontSize: "14px",
  },
  submitButton: {
    flex: 1,
    padding: "12px",
    borderRadius: "8px",
    border: "none",
    backgroundColor: "#10134F",
    color: "#fff",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "600",
  },
};

export default AddModal;
